/**
 * Base58 (Bitcoin/Solana alphabet) — SHARED encode/decode, zero dependencies.
 *
 * Solana identity is base58 everywhere: pubkeys (32 bytes → 32-44 chars), ed25519 signatures (64 bytes →
 * 86-88 chars) and transaction signatures (same 64 bytes). Both halves of the app need it — the client to
 * turn a wallet's `Uint8Array` signature into a wire string, the server to decode a pubkey/signature back
 * into bytes for `crypto.verify` — so it lives here, in the package they already share, rather than pulling
 * `bs58`/`@solana/web3.js` into the browser bundle.
 *
 * ⚠️ Base58 is CASE-SENSITIVE. Nothing in the codebase may `.toLowerCase()` an address, a signature or a
 * transaction id anymore (that was an EVM-hex habit; on Solana it corrupts the value).
 */

export const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

/** Character → value, -1 for anything outside the alphabet (0/O/I/l are deliberately absent). */
const B58_MAP: Int8Array = (() => {
  const m = new Int8Array(128).fill(-1);
  for (let i = 0; i < BASE58_ALPHABET.length; i++) m[BASE58_ALPHABET.charCodeAt(i)] = i;
  return m;
})();

/** A Solana pubkey as it appears on the wire: 32 bytes of base58 → 32-44 chars. Shape only — `base58Decode`
 *  is what proves it is really 32 bytes (a 44-char string can overflow into 33). */
export const SOLANA_PUBKEY_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
/** A 64-byte ed25519 signature (message signature OR transaction id) in base58: 86-88 chars. */
export const SOLANA_SIG_RE = /^[1-9A-HJ-NP-Za-km-z]{64,90}$/;

/** Decode a base58 string to bytes. Returns null on any character outside the alphabet (never throws). */
export function base58Decode(str: string): Uint8Array | null {
  if (str.length === 0) return new Uint8Array(0);
  const bytes: number[] = [];
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i);
    const val = code < 128 ? B58_MAP[code]! : -1;
    if (val < 0) return null;
    let carry = val;
    for (let j = 0; j < bytes.length; j++) {
      carry += bytes[j]! * 58;
      bytes[j] = carry & 0xff;
      carry >>>= 8;
    }
    while (carry > 0) { bytes.push(carry & 0xff); carry >>>= 8; }
  }
  // Each leading '1' is one leading zero byte.
  for (let i = 0; i < str.length && str[i] === '1'; i++) bytes.push(0);
  return new Uint8Array(bytes.reverse());
}

/** Encode bytes as base58. */
export function base58Encode(bytes: Uint8Array): string {
  if (bytes.length === 0) return '';
  const digits: number[] = [];
  for (let i = 0; i < bytes.length; i++) {
    let carry = bytes[i]!;
    for (let j = 0; j < digits.length; j++) {
      carry += digits[j]! << 8;
      digits[j] = carry % 58;
      carry = (carry / 58) | 0;
    }
    while (carry > 0) { digits.push(carry % 58); carry = (carry / 58) | 0; }
  }
  let out = '';
  for (let i = 0; i < bytes.length && bytes[i] === 0; i++) out += '1';
  for (let i = digits.length - 1; i >= 0; i--) out += BASE58_ALPHABET[digits[i]!];
  return out;
}

/** True iff `s` is a well-formed Solana pubkey: base58 alphabet, 32-44 chars, and EXACTLY 32 bytes decoded. */
export function isSolanaPubkey(s: string): boolean {
  if (!SOLANA_PUBKEY_RE.test(s)) return false;
  const b = base58Decode(s);
  return b !== null && b.length === 32;
}

/** True iff `s` decodes to exactly 64 bytes — an ed25519 message signature or a transaction signature. */
export function isSolanaSignature(s: string): boolean {
  if (!SOLANA_SIG_RE.test(s)) return false;
  const b = base58Decode(s);
  return b !== null && b.length === 64;
}
